import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import defaultThumbnailImage from "@assets/images/default-thumbnail-insight.png";
import { Path } from "@core/routing/path";
import { Article } from "@domain/lms/article/article";
import { Category } from "@domain/lms/category/category";
import { ImageVersions } from "@domain/media/dtos";
import { useHover, useIsMobile } from "@utils/hooks";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { BadgeLabel } from "@ui/components/badgeLabel";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { CourseStatusLabel } from "@ui/components/course/courseStatusLabel";
import { InsightLargeIcons, InsightSmallIcons } from "@ui/components/icons";
import { ArticleThumbnailMenu } from "./articleThumbnailMenu";

interface ArticleThumbnailHaiderProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
	isSmall?: boolean;
	displayStatus?: boolean;
}
const renderCategorieLabel = (isSmall: boolean, category: Category): JSX.Element => {
	if (isSmall)
		return (
			<StyledCategoryLabel
				category={category}
				isSmall={isSmall}
				renderIcons={() => <InsightSmallIcons color={theme.colors.white} />}
				isTitleTruncated={true}
			/>
		);
	return <StyledCategoryLabel category={category} />;
};
export const ArticleThumbnailHeader: React.FC<ArticleThumbnailHaiderProps> = ({
	article,
	isSmall,
	displayStatus,
	...htmlProps
}) => {
	const { banner, category, status } = article;
	const { ref, isHovered } = useHover();
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps} ref={ref}>
			<Link to={Path.ReferenceArticle({ id: article.id, slug: article.slug, category: article.category })}>
				<StyledBackgroundImage
					isHovered={isHovered}
					image={banner}
					defaultUrl={!banner ? defaultThumbnailImage : undefined}
					size={ImageVersions.MEDIUM}
					backdrop
					isBackLinearGradient
				/>
			</Link>
			{category && category !== "OTHERS" && renderCategorieLabel(isMobile, category)}
			<StyledMenu blur article={article} />
			{!isMobile && (
				<ContentBadge
					renderIcons={() => <InsightLargeIcons />}
					badgeTitle={I18n.t("article.thumbnail.badge_label")}
					isHovered={isHovered}
					hoverWidth={130}
				/>
			)}
			{displayStatus && <ArticleStatus status={status} />}
		</Container>
	);
};

const Container = styled.div`
	position: relative;
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	border-top-left-radius: 13px;
	border-top-right-radius: 13px;
	overflow: hidden;
	height: 210px;
	@media ${mobile} {
		height: 235px;
	}
`;

const StyledMenu = styled(ArticleThumbnailMenu)`
	position: absolute;
	right: 20px;
	top: 20px;

	@media (max-width: 900px) {
		right: 10px;
		top: 10px;
	}
`;

const ContentBadge = styled(BadgeLabel)`
	position: absolute;
	top: 0px;
	left: 20px;

	@media (max-width: 900px) {
		left: 10px;
		top: 0px;
	}
`;

const StyledCategoryLabel = styled(CategoryLabel)<{ isSmall?: boolean }>`
	position: absolute;
	${({ isSmall }) =>
		isSmall
			? css`
					left: 20px;
					top: 10px;
				`
			: css`
					right: 64px;
					top: 20px;
				`};
`;

const ArticleStatus = styled(CourseStatusLabel)`
	position: absolute;
	right: 20px;
	bottom: 15px;
`;
